import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import api, { asArray } from '../services/api';
import Logo from './Logo';
import { getSignalsLastSeenKey, markSignalsSeen as sharedMarkSignalsSeen } from '../utils/signalAlerts';
import { isPushSupported, isPushPrefOn, enablePush, disablePush, forgetDevice } from '../utils/pushNotifications';
import { Capacitor } from '@capacitor/core';

/** iOS app only: Apple requires in-app account deletion; web and Android menus are unchanged. */
const IS_IOS_APP = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

function formatAccountNo(user) {
  if (user?.id == null) return '—';
  let year = new Date().getFullYear();
  if (user.createdAt) {
    const y = new Date(user.createdAt).getFullYear();
    if (!Number.isNaN(y)) year = y;
  }
  return `10${year}${user.id}`;
}

/** Menu section separator (see the *Links arrays in Sidebar). */
const DIVIDER = 'divider';

/** Simple line icons (inline SVG) */
const Icons = {
  signals: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  ai: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  market: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16h16" />
    </svg>
  ),
  sectors: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  ),
  quote: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  earnings: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  news: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  ),
  education: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    </svg>
  ),
  help: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  refer: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  plan: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  legal: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  users: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  manage: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  messages: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  ),
  pl: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  revenue: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  admin: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  key: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  ),
  star: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  logout: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  weekly: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6m-6 4h10M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  bell: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  // Distinct from "bell" on purpose — the consumer "new published signal" bell and the
  // staff "needs review" bell used to render the SAME glyph (only the badge color
  // differed), so on the admin/staff view two identical bells sat side by side. This
  // "watch" (eye) icon is used for the staff review alert instead, so the two are
  // visually distinguishable at a glance, not just by badge color.
  watch: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  // Dropdown-menu link icon for the user-facing Notifications tab / admin's
  // "Manage Notifications" console — distinct from "bell" (published signals)
  // and "watch" (staff review queue) so all three read differently at a glance.
  notify: (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  ),
  // Header bell for system/admin alerts (scan-engine failures, loss-limit trips)
  // — a warning-triangle glyph so it's visually distinct from "bell" (new
  // published signals) and "watch" (staff review queue) sitting next to it.
  sysalert: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
};

function formatIndexPct(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function formatIndexPrice(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : n.toFixed(2);
}

export default function Sidebar() {
  const { user, logout, refreshUser } = useAuth();
  const { marketPageEnabled } = useSettings();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const [indices, setIndices] = useState([]);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // Android push: register this phone after login (asks for Android's notification
  // permission the first time) unless the user switched popups off in the menu.
  const pushSupported = isPushSupported();
  const [pushOn, setPushOn] = useState(isPushPrefOn());
  const [pushNote, setPushNote] = useState('');
  useEffect(() => {
    if (!user?.id || !pushSupported || !isPushPrefOn()) return;
    enablePush((route) => navigate(route)).then((r) => {
      if (!r.ok && r.reason === 'denied') setPushNote('Allow notifications for StockXignal in Android Settings');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pushSupported]);

  const togglePush = async () => {
    setPushNote('');
    if (pushOn) {
      await disablePush();
      setPushOn(false);
    } else {
      const r = await enablePush((route) => navigate(route));
      setPushOn(r.ok);
      if (!r.ok) setPushNote(r.reason === 'denied'
        ? 'Allow notifications for StockXignal in Android Settings'
        : 'Could not turn on notifications');
    }
  };

  // Live index strip on black bar — always on while logged in (Yahoo/Finnhub backup)
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = () => {
      api.get('/stock/indices')
        .then((r) => {
          if (cancelled) return;
          if (r.data?.success && Array.isArray(r.data.indices)) {
            setIndices(r.data.indices);
          }
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  // Red "watch" alert: unreviewed "AI Rapid Profit" signals PLUS auto-generated
  // scalp/swing picks awaiting review (admin/semi-admin only) — was AI Rapid Profit
  // only, so the scalp/swing auto-generation added elsewhere never surfaced here.
  const [alertCount, setAlertCount] = useState(0);
  // Signal publish/close notification drafts waiting in Manage Notifications' Review.
  const [draftCount, setDraftCount] = useState(0);
  const isStaffRole = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_SEMI_ADMIN';
  useEffect(() => {
    if (!isStaffRole) return undefined;
    let cancelled = false;
    const poll = () => {
      Promise.all([
        api.get('/weekly-signals/alerts/count').catch(() => ({ data: { count: 0 } })),
        api.get('/signals/scalp/pending-count').catch(() => ({ data: { count: 0 } })),
        api.get('/notifications/drafts').catch(() => ({ data: [] })),
      ]).then(([weekly, scalp, drafts]) => {
        if (cancelled) return;
        setAlertCount((weekly.data?.count || 0) + (scalp.data?.count || 0));
        setDraftCount(asArray(drafts.data).length);
      }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isStaffRole]);

  // Blue bell alert: newly PUBLISHED signals (any signed-in user) the person hasn't
  // opened "AI Rapid Profit" to see yet. Tracked per-account via localStorage against
  // the newest publishedAt timestamp seen. Clears when the bell is clicked, AND when
  // the user visits Signals.jsx or WeeklySignals.jsx directly (e.g. via SignalTabsNav
  // instead of the bell) — those pages call the same markSignalsSeen() and fire the
  // 'sx-signals-seen' event this listens for, so the badge doesn't linger stale.
  const [newSignalCount, setNewSignalCount] = useState(0);
  const newSignalKey = getSignalsLastSeenKey(user);
  useEffect(() => {
    if (!user || !newSignalKey) return undefined;
    let cancelled = false;
    const poll = () => {
      Promise.all([
        api.get('/weekly-signals/published').catch(() => ({ data: [] })),
        api.get('/signals/published').catch(() => ({ data: [] })),
      ]).then(([weekly, plain]) => {
        if (cancelled) return;
        const all = [...asArray(weekly.data), ...asArray(plain.data)];
        const lastSeen = Number(localStorage.getItem(newSignalKey) || 0);
        const count = all.filter((s) => {
          const t = s.publishedAt ? new Date(s.publishedAt).getTime() : 0;
          return t > lastSeen;
        }).length;
        setNewSignalCount(count);
      }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 30_000);
    window.addEventListener('sx-signals-seen', poll);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('sx-signals-seen', poll); };
  }, [user, newSignalKey]);

  const markSignalsSeen = () => sharedMarkSignalsSeen(user);

  // System/admin alert bell (scan-engine failures, portfolio loss-limit trips —
  // AdminAlertService on the backend). This was previously built into Navbar.jsx,
  // a component that is never rendered anywhere in the app, so the feature was
  // invisible; it belongs here in Sidebar, the nav that's actually shown.
  const [sysAlertCount, setSysAlertCount] = useState(0);
  const [sysAlertOpen, setSysAlertOpen] = useState(false);
  const [sysAlerts, setSysAlerts] = useState([]);
  const [sysAlertsLoading, setSysAlertsLoading] = useState(false);
  const sysAlertRef = useRef(null);

  useEffect(() => {
    if (!isStaffRole) return undefined;
    let cancelled = false;
    const poll = () => {
      api.get('/admin-alerts/unread-count').then((res) => {
        if (cancelled) return;
        setSysAlertCount(res.data?.count || 0);
      }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isStaffRole]);

  useEffect(() => {
    const onDoc = (e) => {
      if (sysAlertRef.current && !sysAlertRef.current.contains(e.target)) setSysAlertOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openSysAlerts = () => {
    const next = !sysAlertOpen;
    setSysAlertOpen(next);
    if (next) {
      setSysAlertsLoading(true);
      api.get('/admin-alerts?limit=20').then((res) => setSysAlerts(asArray(res.data)))
        .catch(() => {})
        .finally(() => setSysAlertsLoading(false));
    }
  };

  const ackSysAlert = (id) => {
    api.post(`/admin-alerts/${id}/ack`).then(() => {
      setSysAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, acknowledged: true } : a)));
      setSysAlertCount((prev) => Math.max(0, prev - 1));
    }).catch(() => {});
  };

  const ackAllSysAlerts = () => {
    api.post('/admin-alerts/ack-all').then(() => {
      setSysAlerts((prev) => prev.map((a) => ({ ...a, acknowledged: true })));
      setSysAlertCount(0);
    }).catch(() => {});
  };

  if (!user) return null;

  const isAdmin = user.role === 'ROLE_ADMIN';
  const isSemi = user.role === 'ROLE_SEMI_ADMIN';

  // [to, label, iconKey] — Reset password and Log out moved under the user card
  // below (not in this list) since they're account actions, not navigation.
  // '/weekly-signals' and '/ai-signals' were here too — removed since Signals.jsx,
  // WeeklySignals.jsx, and AiSignals.jsx now all carry SignalTabsNav (a page-level
  // tab bar switching between the 3), so the user no longer needs the dropdown for
  // this specific switch.
  // DIVIDER = a thin line between menu sections.
  // Users: trading tools (same order as the phone bottom bar) → learn & grow →
  // support & account. Admin/semi-admin: Manage Signals first, then their own
  // management tools → trading tools → legal.
  const userLinks = [
    ['/signals', 'Signals', 'signals'],
    ['/notifications', 'Notifications', 'notify'],
    ['/market', 'Market', 'market'],
    ['/stock-price', 'Live Quote', 'quote'],
    ['/earnings', 'Earnings', 'earnings'],
    ['/news', 'News', 'news'],
    ['/sector-rotation', 'Sector rotation', 'sectors'],
    DIVIDER,
    ['/education', 'Education', 'education'],
    ['/refer', 'Refer a friend', 'refer'],
    DIVIDER,
    ['/help', 'Help', 'help'],
    ['/rate', 'Rate app', 'star'],
    ['/subscribe', 'Plan', 'plan'],
    ['/legal', 'Terms & Legal', 'legal'],
    ...(IS_IOS_APP ? [['/delete-account', 'Delete account', 'legal']] : []),
  ];

  const adminLinks = [
    ['/admin/signals', 'Manage Signals', 'manage'],
    ['/admin/notifications', 'Manage Notifications', 'notify'],
    ['/admin', 'Admin home', 'admin'],
    ['/admin/users', 'Users', 'users'],
    ['/admin/messages', 'Messages', 'messages'],
    ['/admin/performance', 'P/L', 'pl'],
    ['/admin/signal-history', 'Signal History', 'manage'],
    ['/admin/revenue', 'Revenue', 'revenue'],
    ['/admin/pricing', 'Plan pricing', 'plan'],
    ['/admin/ratings', 'App ratings', 'star'],
    DIVIDER,
    ['/signals', 'Signals', 'signals'],
    ['/admin/market', 'Market', 'market'],
    ['/stock-price', 'Live Quote', 'quote'],
    ['/earnings', 'Earnings', 'earnings'],
    ['/news', 'News', 'news'],
    ['/sector-rotation', 'Sector rotation', 'sectors'],
    DIVIDER,
    ['/legal', 'Terms & Legal', 'legal'],
  ];

  const semiLinks = [
    ['/admin/signals', 'Manage Signals', 'manage'],
    ['/admin/notifications', 'Manage Notifications', 'notify'],
    DIVIDER,
    ['/signals', 'Signals', 'signals'],
    ['/market', 'Market', 'market'],
    ['/stock-price', 'Live Quote', 'quote'],
    ['/earnings', 'Earnings', 'earnings'],
    ['/news', 'News', 'news'],
    ['/sector-rotation', 'Sector rotation', 'sectors'],
    DIVIDER,
    ['/help', 'Help', 'help'],
    ['/rate', 'Rate app', 'star'],
    ['/legal', 'Terms & Legal', 'legal'],
  ];

  const mustPay = !isAdmin && !isSemi && (user.needsPayment || user.subscriptionActive === false);
  // Expired trial: only subscription page is available
  const paywallLinks = [
    ['/subscribe', 'Subscribe / Activate', 'plan'],
    ['/legal', 'Terms & Legal', 'legal'],
  ];
  const baseLinks = mustPay ? paywallLinks : (isAdmin ? adminLinks : isSemi ? semiLinks : userLinks);
  // Admin can switch the Market page off for users (Admin home → Site settings);
  // marketPageEnabled is always true for admin/semi-admin (see SettingsContext).
  const links = marketPageEnabled ? baseLinks : baseLinks.filter((item) => item === DIVIDER || item[0] !== '/market');

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white border-b border-slate-800">
      <div className="container mx-auto px-3 sm:px-4 max-w-5xl flex items-center gap-2 h-[3.75rem]">
        {/* Major indices — always visible on the black bar */}
        <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
          <div className="flex items-center gap-3 sm:gap-4 whitespace-nowrap pr-2 text-[11px] sm:text-xs">
            {indices.length === 0 ? (
              <span className="text-slate-500 tabular-nums">Markets…</span>
            ) : (
              indices.map((ix) => {
                const up = ix.up !== false && Number(ix.change) >= 0;
                const sym = ix.symbol || '';
                return (
                  <Link
                    key={sym}
                    to={`/stock/${encodeURIComponent(ix.ticker || sym)}`}
                    className="inline-flex items-center gap-1.5 shrink-0 hover:opacity-90 transition"
                    title={ix.name || sym}
                  >
                    <span className="font-semibold text-slate-200 hover:text-white hover:underline">{sym}</span>
                    <span className={`tabular-nums ${up ? 'text-emerald-400' : 'text-rose-400'}`}>{formatIndexPrice(ix.price)}</span>
                    <span
                      className={`inline-flex items-center gap-0.5 tabular-nums font-medium ${
                        up ? 'text-emerald-400' : 'text-rose-400'
                      }`}
                    >
                      <span aria-hidden>{up ? '▲' : '▼'}</span>
                      {formatIndexPct(ix.changePercent)}
                    </span>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {user && (
          <button
            type="button"
            onClick={() => {
              markSignalsSeen();
              navigate('/signals');
            }}
            className="relative shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition"
            title="New published signals"
          >
            {Icons.bell}
            {newSignalCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {newSignalCount > 9 ? '9+' : newSignalCount}
              </span>
            )}
          </button>
        )}

        {isStaffRole && (
          <button
            type="button"
            onClick={() => navigate('/admin/signals')}
            className="relative shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition"
            title="Signals needing review — AI Rapid Profit + scalp/swing"
          >
            {Icons.watch}
            {alertCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {alertCount > 9 ? '9+' : alertCount}
              </span>
            )}
          </button>
        )}

        {isStaffRole && (
          <div className="relative shrink-0" ref={sysAlertRef}>
            <button
              type="button"
              onClick={openSysAlerts}
              className="relative w-8 h-8 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition"
              title="System alerts — scan failures, loss-limit trips"
            >
              {Icons.sysalert}
              {sysAlertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">
                  {sysAlertCount > 99 ? '99+' : sysAlertCount}
                </span>
              )}
            </button>
            {sysAlertOpen && (
              <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-700 bg-slate-900 shadow-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/80 flex items-center justify-between">
                  <p className="font-semibold text-sm text-white">System alerts</p>
                  {sysAlerts.some((a) => !a.acknowledged) && (
                    <button type="button" onClick={ackAllSysAlerts} className="text-[11px] text-emerald-400 hover:text-emerald-300 font-semibold">
                      Acknowledge all
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {sysAlertsLoading ? (
                    <p className="text-center text-slate-500 text-xs py-8">Loading…</p>
                  ) : sysAlerts.length === 0 ? (
                    <p className="text-center text-slate-500 text-xs py-8">No alerts.</p>
                  ) : (
                    sysAlerts.map((a) => (
                      <div
                        key={a.id}
                        className={`px-4 py-2.5 border-b border-slate-800 last:border-b-0 ${a.acknowledged ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                            a.severity === 'CRITICAL' ? 'bg-rose-400/15 text-rose-300'
                              : a.severity === 'WARNING' ? 'bg-amber-400/15 text-amber-300'
                              : 'bg-sky-400/15 text-sky-300'
                          }`}
                          >
                            {a.severity}
                          </span>
                          {!a.acknowledged && (
                            <button
                              type="button"
                              onClick={() => ackSysAlert(a.id)}
                              className="text-[11px] text-slate-400 hover:text-white shrink-0"
                            >
                              Ack
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-slate-200 mt-1 leading-snug">{a.message}</p>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="relative shrink-0" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setOpen((v) => !v);
              if (!open) refreshUser?.();
            }}
            className="inline-flex items-center gap-2 font-display font-bold tracking-tight rounded-lg px-2 py-2 hover:bg-white/10 transition select-none touch-manipulation cursor-pointer"
            aria-expanded={open}
          >
            {/* pointer-events-none on the children so a tap on the logo or the name
                always lands on the button itself — on the Android WebView, taps on
                the SVG/text weren't reliably reaching the button, so only the
                chevron area opened the menu. */}
            <span className="relative pointer-events-none">
              <Logo size={22} />
              {isStaffRole && draftCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-slate-900" />
              )}
            </span>
            <span className="pointer-events-none">StockXignal</span>
            <svg
              className={`w-3.5 h-3.5 text-slate-400 transition pointer-events-none ${open ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-64 rounded-xl border border-slate-700 bg-slate-900 shadow-xl z-50 max-h-[80vh] overflow-y-auto py-1">
              {links.map((item, i) => {
                if (item === DIVIDER) {
                  return <div key={`div-${i}`} className="my-1 border-t border-slate-700/70" role="separator" />;
                }
                const [to, label, iconKey] = item;
                const icon = Icons[iconKey] || Icons.signals;
                const active =
                  loc.pathname === to || (to !== '/signals' && to !== '/forgot-password' && loc.pathname.startsWith(to));
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`flex items-center gap-2.5 px-3 py-2 text-sm transition ${
                      active
                        ? 'bg-brand-500/20 text-brand-300 font-semibold'
                        : 'text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    {icon}
                    {label}
                    {to === '/admin/notifications' && draftCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">
                        {draftCount > 9 ? '9+' : draftCount}
                      </span>
                    )}
                  </Link>
                );
              })}

              {/* Compact user card, with "Reset password" right underneath it (an
                  account action, not navigation). Log out is deliberately kept
                  separate below — a divider keeps it from reading as part of the
                  user panel, since it's an exit action, not an account detail. */}
              <div className="border-t border-slate-700 m-2 mt-1 rounded-xl bg-slate-800/80 px-3 py-2.5">
                <p className="font-semibold text-sm text-white truncate">{user.fullName || 'User'}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
                <div className="mt-1.5 flex justify-between text-[11px] text-slate-400">
                  <span>{(user.role || '').replace('ROLE_', '')}</span>
                  <span className={user.subscriptionActive ? 'text-emerald-400' : 'text-rose-400'}>
                    {user.subscriptionActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">Acct {formatAccountNo(user)}</p>
              </div>
              {pushSupported && (
                <div className="mx-2 px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2.5 text-sm text-slate-200">
                      {Icons.bell}
                      Push notifications
                    </span>
                    <button
                      type="button"
                      onClick={togglePush}
                      aria-pressed={pushOn}
                      className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${pushOn ? 'bg-emerald-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${pushOn ? 'translate-x-4' : ''}`} />
                    </button>
                  </div>
                  {pushNote && <p className="mt-1 text-[11px] text-amber-300">{pushNote}</p>}
                </div>
              )}
              <Link
                to="/forgot-password"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 mx-2 px-3 py-2 text-sm rounded-lg text-slate-200 hover:bg-white/10 transition"
              >
                {Icons.key}
                Reset password
              </Link>
              <button
                type="button"
                onClick={async () => {
                  setOpen(false);
                  await forgetDevice(); // this phone stops getting pushes for this account
                  logout();
                  navigate('/login');
                }}
                className="w-[calc(100%-1rem)] flex items-center gap-2.5 mx-2 mt-1 px-3 py-2 mb-1 text-sm rounded-lg border-t border-slate-700 pt-2.5 text-slate-200 hover:bg-white/10 text-left transition"
              >
                {Icons.logout}
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
